export const SUDOKU_SIZE = 6;
export const SUDOKU_CELL_COUNT = 36;
export const SUDOKU_DIGITS = [1, 2, 3, 4, 5, 6] as const;

export type SudokuDigit = (typeof SUDOKU_DIGITS)[number];
export type SudokuValue = 0 | SudokuDigit;

export type MiniSudokuPuzzle = {
  puzzle: SudokuValue[];
  solution: SudokuDigit[];
};

export function isMiniSudokuPuzzle(value: unknown): value is MiniSudokuPuzzle {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<MiniSudokuPuzzle>;
  const structurallyValid =
    Array.isArray(candidate.puzzle) &&
    candidate.puzzle.length === SUDOKU_CELL_COUNT &&
    candidate.puzzle.every((cell) => Number.isInteger(cell) && cell! >= 0 && cell! <= 6) &&
    Array.isArray(candidate.solution) &&
    candidate.solution.length === SUDOKU_CELL_COUNT &&
    candidate.solution.every((cell) => Number.isInteger(cell) && cell! >= 1 && cell! <= 6) &&
    candidate.puzzle.every((cell, index) => cell === 0 || cell === candidate.solution![index]);

  if (!structurallyValid) return false;

  const solution = candidate.solution as SudokuDigit[];
  const isCompleteUnit = (indices: number[]) =>
    indices
      .map((index) => solution[index])
      .sort((a, b) => a - b)
      .every((digit, index) => digit === index + 1);

  for (let index = 0; index < SUDOKU_SIZE; index += 1) {
    const row = Array.from({ length: SUDOKU_SIZE }, (_, column) => index * SUDOKU_SIZE + column);
    const column = Array.from(
      { length: SUDOKU_SIZE },
      (_, rowIndex) => rowIndex * SUDOKU_SIZE + index,
    );
    if (!isCompleteUnit(row) || !isCompleteUnit(column)) return false;
  }

  for (let boxRow = 0; boxRow < 3; boxRow += 1) {
    for (let boxColumn = 0; boxColumn < 2; boxColumn += 1) {
      const box = Array.from({ length: SUDOKU_SIZE }, (_, offset) => {
        const row = boxRow * 2 + Math.floor(offset / 3);
        const column = boxColumn * 3 + (offset % 3);
        return row * SUDOKU_SIZE + column;
      });
      if (!isCompleteUnit(box)) return false;
    }
  }

  return true;
}
